/**
 * Rhino-1 Challenges Widget
 * Dashboard widget showing user's challenge progress
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { useAuth } from '../../contexts/AuthContext';
import {
  Rocket, ChevronRight, Target, CheckCircle2, Circle, Star, Zap
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Mini Challenge Card
const MiniChallengeCard = ({ challenge, onClick }) => {
  const isCompleted = challenge.status === 'COMPLETED';
  const isInProgress = challenge.status === 'IN_PROGRESS';
  
  return (
    <div 
      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
        isCompleted 
          ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800'
          : isInProgress
            ? 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800'
            : 'hover:bg-accent/50'
      }`}
      onClick={onClick}
    >
      <div className="flex items-center gap-3">
        <div className={`p-1.5 rounded-lg ${
          isCompleted ? 'bg-green-100 dark:bg-green-900/50' : 
          isInProgress ? 'bg-blue-100 dark:bg-blue-900/50' : 'bg-slate-100 dark:bg-slate-800'
        }`}>
          {isCompleted ? (
            <CheckCircle2 className="h-4 w-4 text-green-600" />
          ) : isInProgress ? (
            <Zap className="h-4 w-4 text-blue-600" />
          ) : (
            <Circle className="h-4 w-4 text-slate-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{challenge.title}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <Progress 
              value={challenge.progress_percent || 0} 
              className="h-1 flex-1"
            />
            <span className="text-xs text-muted-foreground">
              {Math.round(challenge.progress_percent || 0)}%
            </span>
          </div>
        </div>
        <Badge variant="outline" className="text-xs shrink-0">
          <Star className="h-3 w-3 mr-1 text-amber-500" />
          {challenge.points_reward}
        </Badge>
      </div>
    </div>
  );
};

export const RhinoChallengesWidget = ({ className = '' }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.id) return;
      
      try {
        const response = await axios.get(`${API}/rhino-challenges/widget/data?user_id=${user.id}`);
        setData(response.data);
      } catch (error) {
        console.error('Error fetching challenges widget data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.id]);

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 w-32 bg-slate-200 dark:bg-slate-800 rounded" />
            <div className="h-12 bg-slate-200 dark:bg-slate-800 rounded" />
            <div className="h-12 bg-slate-200 dark:bg-slate-800 rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className} data-testid="rhino-challenges-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Rocket className="h-5 w-5 text-blue-500" />
            Rhino-1 Challenges
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/competition/challenges')}
          >
            View All
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Progress Summary */}
        {data && (
          <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Platform Mastery</span>
              <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                {Math.round(data.completion_percent || 0)}%
              </span>
            </div>
            <Progress value={data.completion_percent || 0} className="h-2 [&>div]:bg-blue-500" />
            <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
              <span>{data.completed_challenges}/{data.total_challenges} completed</span>
              <span className="flex items-center gap-1">
                <Star className="h-3 w-3 text-amber-500" />
                {data.total_points} pts
              </span>
            </div>
          </div>
        )}

        {/* Recommended Challenges */}
        {data?.recommended_challenges?.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Recommended for You
            </p>
            {data.recommended_challenges.slice(0, 3).map(challenge => (
              <MiniChallengeCard
                key={challenge.id}
                challenge={challenge}
                onClick={() => navigate('/competition/challenges')}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-6">
            <Target className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-sm text-muted-foreground mb-3">
              {data?.completed_challenges === data?.total_challenges 
                ? 'All challenges completed! 🎉'
                : 'No challenges available'}
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/competition/challenges')}
            >
              <Rocket className="h-4 w-4 mr-2" />
              Explore Challenges
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RhinoChallengesWidget;
